<script setup lang="ts">
import { computed } from 'vue';
import DOMPurify from 'dompurify';
import VueMarkdown from 'vue-markdown';

const props = defineProps<{
  title: string;
  tags?: string[];
  content?: string;
}>();

const looksLikeHtml = (value: string) => /<[^>]+>/.test(value);
const isHtmlContent = computed(() => Boolean(props.content && looksLikeHtml(props.content)));

const sanitizedHtmlContent = computed(() =>
  isHtmlContent.value && props.content ? DOMPurify.sanitize(props.content) : ''
);
</script>

<template>
  <div>
    <div class="tw:flex tw:max-md:flex-col tw:gap-4">
      <div class="tw:typography-h1 tw:md:typography-h2">{{ title }}</div>

      <div v-if="!!tags?.length">
        <div class="tw:flex tw:items-center tw:gap-2 tw:whitespace-nowrap">
          <div
            v-for="(item, index) in tags"
            :key="index"
            class="tw:py-1 tw:px-3 tw:rounded-sm tw:typography-sidenotes tw:text-main-blue tw:bg-blue-bg"
          >
            {{ item }}
          </div>
        </div>
      </div>
    </div>

    <div class="tw:mt-4 tw:typography-small tw:text-secondary summary-content markdown">
      <slot>
        <div v-if="isHtmlContent" v-html="sanitizedHtmlContent" />
        <VueMarkdown
          v-else
          class="tw:flex tw:flex-col tw:gap-4"
          :source="content || ''"
          :html="false"
          :linkify="true"
          :breaks="true"
        />
      </slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
.summary-content {
  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(p),
  :deep(pre),
  :deep(code),
  :deep(a) {
    margin: 0;
  }

  :deep(h1),
  :deep(h2) {
    font-size: 18px;
    line-height: 1.3;
    color: #242424;
  }

  :deep(h3) {
    font-size: 14px;
    line-height: 1.3;
    font-weight: 500;
    color: #242424;
  }

  :deep(pre) {
    padding: 12px;
    background: #f3f5f8;
    border-radius: 6px;
    overflow-x: auto;
  }

  :deep(code) {
    font-size: 14px;
    line-height: 1.2;
    color: #3180f6;
  }

  :deep(pre code) {
    color: #1f2937;
  }

  :deep(a) {
    color: #3180f6;
    text-decoration: underline;
  }
}
</style>
